import { defineStore } from 'pinia'
import fetchData, { HttpMethod } from '@/model/Storage'
import { ContactState, Contact, Message, FetchContactsSearchParams } from '@/model/types'

// Utility function for handling errors
// Logs a custom message and the error details
const handleError = (error: unknown, customMessage = '') => {
    console.error(customMessage, error instanceof Error ? error.message : error)
}

// Defines the ContactStore using Pinia
export const useContactStore = defineStore('ContactStore', {
    // Initial state of the store
    state: (): ContactState => ({
        contacts: [],
        loading: true,
        selectedContact: null,
        contactsLinks: [],
    }),
    persist: true,

    // Store actions for various functionalities
    actions: {
        // Fetches contacts from the API
        async fetchContacts({ limit, offset, filter }: FetchContactsSearchParams) {
            // Return a promise that resolves when the data is fetched
            return new Promise(async (resolve, reject) => {
                this.loading = true
                try {
                    const response = await fetchData({
                        method: HttpMethod.GET,
                        endpoint: 'contacts?limit=50&sort=contactId',
                    })
                    if (response.contacts) {
                        this.contacts = response.contacts
                        this.contactsLinks = response.links
                        console.log(this.contactsLinks)
                    } else {
                        handleError(
                            null,
                            'Unexpected response structure: ' + JSON.stringify(response)
                        )
                    }
                } catch (error) {
                    handleError(error, 'Error fetching contacts:')
                } finally {
                    this.loading = false
                }
            })
        },

        // Fetches contacts with search parameters
        async fetchContactsSearch({ limit, offset, filter }: FetchContactsSearchParams) {
            this.loading = true
            try {
                let endpoint = `contacts?limit=${limit}&offset=${offset}${
                    filter ? `&search=${filter}` : '&sort=contactId'
                }`
                const response = await fetchData({ method: HttpMethod.GET, endpoint: endpoint })

                if (response && response.contacts) {
                    this.contacts = response.contacts
                } else {
                    handleError(null, 'Unexpected response structure: ' + JSON.stringify(response))
                }
            } catch (error) {
                handleError(error, 'Error in fetchContactsSearch:')
            } finally {
                this.loading = false
            }
        },

        // Fetches contacts with limit and offset
        async fetchContactsLimit({
            limit,
            offset,
            filter,
            done,
        }: {
            limit: number
            offset: number
            filter: string
            done: Function
        }) {
            this.loading = true
            try {
                let endpoint = `contacts?limit=${limit}&offset=${offset}${
                    filter ? `&search=${filter}` : '&sort=contactId'
                }`
                const response = await fetchData({ method: HttpMethod.GET, endpoint: endpoint })

                if (response && response.contacts) {
                    this.contacts.push(...response.contacts)
                    done(response.contacts.length === 0 ? 'empty' : 'ok')
                } else {
                    handleError(null, 'Unexpected response structure: ' + JSON.stringify(response))
                }
            } catch (error) {
                handleError(error, 'Error in fetchContactsLimit:')
            } finally {
                this.loading = false
            }
        },

        // Creates a new contact; also used in dupliation
        async createContact({ contact }: { contact: Contact }) {
            this.loading = true
            try {
                contact.person = contact.person || {}
                contact.person.gender = contact.person.gender || 'NA'

                const response = await fetchData({
                    method: HttpMethod.POST,
                    endpoint: 'contacts',
                    body: JSON.stringify({ data: contact }),
                })

                if (response && response.contact) {
                    this.selectedContact = response.contact
                } else {
                    handleError(null, 'Failed to create contact: ' + JSON.stringify(response))
                }
            } catch (error) {
                handleError(error, 'Error in createContact:')
            } finally {
                location.reload()
                this.loading = false
            }
        },

        // Action to update a contact
        async updateContact({ contact, contactId }: { contact: Contact; contactId: number }) {
            this.loading = true
            try {
                await fetchData({
                    method: HttpMethod.PUT,
                    endpoint: `/contact/${contactId}/`,
                    body: JSON.stringify({ data: contact }),
                })
            } catch (error) {
                handleError(error, 'Error in updateContact:')
            } finally {
                this.loading = false
            }
        },

        // Deletes a contact
        async deleteContact({ contactId }: { contactId: number }) {
            this.loading = true
            try {
                let index = this.contacts.findIndex((item) => item.contactId === contactId)
                if (index !== -1) {
                    await fetchData({
                        method: HttpMethod.DELETE,
                        endpoint: `/contact/${contactId}/`,
                    })
                    this.contacts.splice(index, 1)
                }
            } catch (error) {
                handleError(error, 'Error in deleteContact:')
            } finally {
                this.loading = false
            }
        },

        // Duplicates a contact
        async duplicateContact({ contact }: { contact: Contact }) {
            this.loading = true
            try {
                if (!contact) {
                    throw new Error('duplicateContact called with undefined contact')
                }

                contact = { ...contact, contactId: null, person: {} }
                await this.createContact({ contact })
            } catch (error) {
                handleError(error, 'Error in duplicateContact:')
            } finally {
                this.loading = false
            }
        },

        // Creates a message (Tiptel)
        async createMessage({ message, success }: { message: Message; success: Function }) {
            this.loading = true
            try {
                await fetchData({
                    method: HttpMethod.POST,
                    endpoint: 'contacts/message',
                    body: JSON.stringify({ data: message }),
                })
                success()
            } catch (error) {
                handleError(error, 'Error in createMessage:')
            } finally {
                this.loading = false
            }
        },
    },
})
